import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "~/components/ui/use-toast";
import type { ClientWithSessions } from "~/hooks/queries/use-practitioner-clients";
import { useApiClient } from "~/hooks/use-api-client";
import { captureException } from "~/lib/analytics";
import { logger } from "~/lib/logger";

export function useDeleteSession() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            sessionId,
            clientId,
        }: {
            sessionId: string;
            clientId?: string;
        }) => {
            logger.withMetadata({ sessionId }).info("Deleting session");

            const response = await apiClient.delete(
                `/session/${sessionId}/delete`,
            );

            if (!response.ok) {
                const errorMessage = `Failed to delete session: ${response.status} ${response.statusText}`;
                logger
                    .withMetadata({ sessionId, status: response.status })
                    .error(errorMessage);

                // Try to get more detailed error from response
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorMessage);
                } catch (e) {
                    throw new Error(errorMessage);
                }
            }

            const data = await response.json();
            return { data, sessionId, clientId };
        },
        onSuccess: ({ sessionId, clientId }) => {
            // AIDEV-NOTE: Update cache directly instead of invalidating to avoid UI flicker
            // Update the cache directly by filtering out the deleted session
            if (clientId) {
                queryClient.setQueryData(
                    ["practitioner", "client", clientId],
                    (oldData: ClientWithSessions | undefined) => {
                        if (!oldData) return oldData;
                        return {
                            ...oldData,
                            sessions:
                                oldData.sessions?.filter(
                                    (s) => s.uuid !== sessionId,
                                ) || [],
                        };
                    },
                );
            }

            // Also update practitioner sessions lists (both with and without needsReview filter)
            queryClient.setQueriesData(
                { queryKey: ["practitioner", "sessions"], exact: false },
                (oldData: unknown) => {
                    if (!Array.isArray(oldData)) return oldData;
                    return oldData.filter(
                        (s: { uuid: string }) => s.uuid !== sessionId,
                    );
                },
            );

            // AIDEV-NOTE: Update user sessions cache for client /sessions page
            queryClient.setQueryData(["user-sessions"], (oldData: unknown) => {
                if (!Array.isArray(oldData)) return oldData;
                return oldData.filter(
                    (s: { uuid: string }) => s.uuid !== sessionId,
                );
            });

            // Invalidate the session itself if the user is viewing it when deleting
            queryClient.invalidateQueries({
                queryKey: ["session", sessionId],
                exact: false,
            });

            toast({
                title: "Session deleted",
                description: "The session has been successfully deleted.",
            });
        },
        onError: (error) => {
            // AIDEV-NOTE: Don't report 404 to Sentry - expected when session already deleted
            // (stale UI, double-click, session deleted by another process)
            const is404 = error.message.includes("404");
            if (!is404) {
                captureException("Failed to delete session", error);
            } else {
                logger
                    .withMetadata({ error: error.message })
                    .info(
                        "Delete session returned 404 (session already deleted)",
                    );
            }
            toast({
                variant: "destructive",
                title: "Error",
                description: is404
                    ? "This session no longer exists."
                    : "Failed to delete the session. Please try again later.",
            });
        },
    });
}
